import { useNavigate } from 'react-router-dom';
import { useCallback, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import useModal from 'lib/hooks/useModal';
import { isKioskDevice } from 'lib/helpers/kiosk';
import { ia, mString } from 'lib/helpers/utility';
import useScrollBlock from 'lib/hooks/useScrollBlock';
import { useUIContext } from 'lib/context/UIContext/UIContext';

import Button from 'components/shared/Button/Button';
import { showAlert } from 'components/shared/Alert/Alert';
import ProcessPayment from 'components/Patient/checkin/SecurePayment/components/ProcessPayment';
import { PaymentContextProvider } from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';

import SuccessPayment from '../../SuccessPayment';

import TipPrompt from './components/TipPrompt';
import KioskAutoStart from './components/KioskAutoStart';

const SecurePayment = ({
  amountDue,
  surchargeAmount = 0,
  isKioskInvoice,
  practice,
  patient,
  token,
  onSuccess,
  invoice,
  presetGratuity = 0
}) => {
  const { isOpen, openModal, closeModal } = useModal();
  const [autoStarted, setAutoStarted] = useState(false);
  const paidRef = useRef(false);
  const tipPromptRef = useRef(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { device } = useUIContext();
  const isMobile = device === 'tablet-sm' || device === 'mobile';

  const [blockScroll, allowScroll] = useScrollBlock();

  const exitsToIdle = !!isKioskInvoice && isKioskDevice();

  const exitToIdle = () => navigate('/kiosk/idle');

  const onSuccessHandler = (data) => {
    paidRef.current = true;
    onSuccess(data);
    queryClient.invalidateQueries(['invoice', token]);
  };

  const onError = () => {
    allowScroll();

    showAlert({
      color: 'danger',
      variant: 'lighter',
      title: 'Payment failed',
      message: 'Payment failed. Please try again.',
      icon: 'danger-mono'
    });
  };

  const onFailHandler = (error) => {
    allowScroll();
    closeModal();

    const errorMessage =
      (ia(error) ? error.join(' ') : error) || 'Payment failed. Please try again.';

    showAlert({
      color: 'danger',
      variant: 'filled',
      title: 'Payment failed',
      message: errorMessage,
      icon: 'error-filled'
    });
  };

  const startPayment = useCallback(
    (isAutoStart) => {
      const openPayment = () => {
        setAutoStarted(isAutoStart);
        blockScroll();
        openModal();
      };
      if (tipPromptRef.current) tipPromptRef.current(openPayment);
      else openPayment();
    },
    [blockScroll, openModal]
  );

  const openModalHandler = () => startPayment(false);

  const autoStartHandler = useCallback(() => startPayment(true), [startPayment]);

  const closeModalHandler = () => {
    allowScroll();
    closeModal();
    setAutoStarted(false);
    if (exitsToIdle && !paidRef.current) exitToIdle();
  };

  const isDisabled = invoice?.paid || amountDue <= 0;
  const requiresInvoiceReview = !!practice?.display_settings?.require_invoice_review;

  const getTooltipMessage = () => {
    if (invoice?.paid) {
      return 'This invoice has already been paid';
    }
    if (amountDue <= 0) {
      return 'No amount due';
    }
    return '';
  };

  return (
    <PaymentContextProvider
      amount={amountDue}
      surchargeAmount={surchargeAmount}
      invoiceToken={token}
      patientId={patient?.id}
      practiceId={practice?.id}
      onSuccess={onSuccessHandler}
      onError={onError}
      initialMethod="credit-card">
      <TipPrompt
        promptRef={tipPromptRef}
        displaySettings={practice?.display_settings}
        presetTip={presetGratuity}
      />
      <KioskAutoStart
        enabled={!isDisabled && !requiresInvoiceReview && !!isKioskInvoice}
        onStart={autoStartHandler}
      />
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-end gap-3 border-0 border-t border-solid border-t-neutral-200 bg-white px-8 py-4 sm:px-4">
        {exitsToIdle && (
          <Button
            variant="tertiary"
            size={isMobile ? 'large' : 'medium'}
            text="Cancel"
            onClick={exitToIdle}
          />
        )}
        <Button
          variant="primary"
          size={isMobile ? 'large' : 'medium'}
          text={`Pay ${mString(amountDue)}`}
          onClick={openModalHandler}
          isDisabled={isDisabled}
          tooltip={isDisabled ? getTooltipMessage() : ''}
          wrapperClassName="sm:w-full"
          className="sm:w-full"
        />
      </div>
      <ProcessPayment
        isOpen={isOpen}
        handleClose={closeModalHandler}
        invoice={invoice}
        patientId={patient?.id}
        onSuccess={onSuccessHandler}
        onFail={onFailHandler}
        practice={practice}
        SuccessScreen={SuccessPayment}
        token={token}
        autoStarted={autoStarted}
      />
    </PaymentContextProvider>
  );
};

export default SecurePayment;
